import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from '@tyro/i18n';
import { BookOpenWithTextIcon, TeaCupIcon } from '@tyro/icons';

import { type CalendarGridPeriod, CalendarGridPeriodType } from '@tyro/api';

import {
  type ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MultiSectionDigitalClock } from '@mui/x-date-pickers';
import {
  DateTimePicker,
  type DateTimePickerProps,
  LoadingPlaceholderContainer,
  useDisclosure,
  useMergeRefs,
} from '@tyro/core';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useCalendarGridPeriods } from '../../api/grid-periods';

dayjs.extend(customParseFormat);

const iconByGridType: Record<
  CalendarGridPeriodType,
  ReactElement<SvgIconProps>
> = {
  [CalendarGridPeriodType.Class]: (
    <BookOpenWithTextIcon sx={{ width: 20, height: 20 }} />
  ),
  [CalendarGridPeriodType.Break]: <TeaCupIcon sx={{ width: 20, height: 20 }} />,
};

export type SelectGridPeriodProps = {
  label?: string;
  value?: Dayjs | null | undefined;
  filterDay?: Dayjs | null | undefined;
  periodType?: 'start' | 'end';
  timePickerProps?: Omit<DateTimePickerProps, 'onChange' | 'value'>;
  inputProps?: DateTimePickerProps['inputProps'];
  onChange: (value: Dayjs | null | undefined) => void;
  onSelectPeriod?: (
    period: CalendarGridPeriod & {
      startTimeAsDate: Dayjs;
      endTimeAsDate: Dayjs;
    },
  ) => void;
};

type GridValue = {
  isoDayOfWeek: number;
  startTime: string;
  filterDay?: Dayjs | null;
};

const getGridValue = ({ filterDay, isoDayOfWeek, startTime }: GridValue) => {
  const currentDate = dayjs(filterDay)
    .set('day', isoDayOfWeek)
    .format('YYYY-MM-DD');
  const currentTime = dayjs(startTime, 'HH:mm').format('HH:mm');

  return dayjs(`${currentDate}T${currentTime}`);
};

const getDateTime = (
  date: Dayjs | null | undefined,
  time: Dayjs | string | null | undefined,
) => {
  if (!time) return;
  const timeAsInstance = dayjs(time ?? dayjs(), 'HH:mm');
  if (!timeAsInstance.isValid()) return;

  const currentDate = dayjs(date ?? dayjs()).format('YYYY-MM-DD');
  const currentTime = timeAsInstance.format('HH:mm');

  return dayjs(`${currentDate}T${currentTime}`);
};

const isSameHourAndMinute = (periodA: Dayjs, periodB: Dayjs) =>
  periodA.hour() === periodB.hour() && periodA.minute() === periodB.minute();

export const SelectGridPeriod = forwardRef<
  HTMLInputElement,
  SelectGridPeriodProps
>(
  (
    {
      label,
      timePickerProps,
      filterDay,
      inputProps,
      value,
      periodType = 'start',
      onChange,
      onSelectPeriod,
    },
    ref,
  ) => {
    const { t } = useTranslation(['common', 'calendar']);

    const id = useId();
    const anchorEl = useRef<HTMLInputElement | null>(null);
    const popperRef = useRef<HTMLDivElement | null>(null);
    const refs = useMergeRefs(ref, anchorEl);
    const clockRef = useRef<HTMLDivElement | null>(null);
    const periodsBtnsRef = useRef<HTMLButtonElement[][]>([]);
    const tabListBtnRef = useRef<HTMLButtonElement[]>([]);

    const { data: gridData = [], isLoading } = useCalendarGridPeriods({});

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [tab, setTab] = useState<string | 'time'>('time');

    const filteredPeriods = useMemo(() => {
      if (!filterDay) return gridData;

      return gridData.map((grid) => ({
        ...grid,
        days: (grid.days || []).filter(
          (day) => day?.isoDayOfWeek === filterDay.day(),
        ),
      }));
    }, [gridData, filterDay]);

    useEffect(() => {
      if (tab === 'time' && !value) {
        if (clockRef.current) {
          const scrollElement = clockRef.current.querySelector(
            '.MuiMultiSectionDigitalClockSection-root',
          );

          scrollElement
            ?.querySelectorAll('.MuiMultiSectionDigitalClockSection-item')
            ?.item(9)
            ?.scrollIntoView({ behavior: 'instant', block: 'start' });

          scrollElement?.scrollBy(0, -4);
        }
      }
    }, [tab, value]);

    useEffect(() => {
      onChange(getDateTime(filterDay, value));
    }, [filterDay]);

    const getMatchedPeriod = useCallback(
      (newValue: Dayjs | null | undefined) => {
        const matchedPeriod = gridData
          .flatMap((periodValue) =>
            (periodValue.days || []).flatMap((day) =>
              (day?.periods || []).map((period) => ({
                isoDayOfWeek: day?.isoDayOfWeek ?? 0,
                ...period,
              })),
            ),
          )
          .find(({ isoDayOfWeek, startTime, endTime }) => {
            const gridValue = getGridValue({
              isoDayOfWeek,
              filterDay,
              startTime: periodType === 'start' ? startTime : endTime,
            });

            return newValue?.isSame(gridValue);
          });

        return matchedPeriod;
      },
      [filterDay, gridData, periodType],
    );

    const checkTabValue = useCallback(
      (newValue: Dayjs | null | undefined) => {
        const matchedPeriod = getMatchedPeriod(newValue);

        if (matchedPeriod || !newValue) {
          const [initialGrid] = gridData;
          setTab(String(initialGrid?.gridIdx));
        } else {
          setTab('time');
        }
      },
      [getMatchedPeriod, gridData],
    );

    const handleChangeValue = useCallback(
      (currentValue: Dayjs | null | undefined) => {
        const newValue = getDateTime(filterDay, currentValue);
        onChange(newValue);
        checkTabValue(newValue);

        const matchedPeriod = getMatchedPeriod(newValue);
        if (matchedPeriod) {
          onSelectPeriod?.({
            ...matchedPeriod,
            startTimeAsDate: getDateTime(filterDay, matchedPeriod.startTime)!,
            endTimeAsDate: getDateTime(filterDay, matchedPeriod.endTime)!,
          });
        }
      },
      [filterDay, onChange, getMatchedPeriod, checkTabValue, onSelectPeriod],
    );

    useEffect(() => {
      if (isOpen) {
        checkTabValue(value);
      }
    }, [checkTabValue, isOpen, value]);

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
      dayIndex: number,
      periodIndex: number,
    ) => {
      const totalDays = periodsBtnsRef.current.length;
      const totalPeriods = periodsBtnsRef.current[dayIndex]?.length || 0;

      const nextDayIndex = (dayIndex + 1) % totalDays;
      const prevDayIndex = (dayIndex - 1 + totalDays) % totalDays;
      const nextPeriodIndex = (periodIndex + 1) % totalPeriods;
      const prevPeriodIndex = (periodIndex - 1 + totalPeriods) % totalPeriods;

      switch (event.key) {
        case 'ArrowRight': {
          periodsBtnsRef.current[nextDayIndex]?.[
            dayIndex + 1 >= totalDays ? nextPeriodIndex : periodIndex
          ]?.focus();
          break;
        }
        case 'ArrowLeft': {
          periodsBtnsRef.current[prevDayIndex]?.[
            dayIndex - 1 < 0 ? prevPeriodIndex : periodIndex
          ]?.focus();
          break;
        }
        case 'ArrowDown': {
          periodsBtnsRef.current[
            periodIndex + 1 >= totalPeriods ? nextDayIndex : dayIndex
          ]?.[nextPeriodIndex]?.focus();
          break;
        }
        case 'ArrowUp': {
          const prevDay = periodIndex - 1 < 0;
          periodsBtnsRef.current[prevDay ? prevDayIndex : dayIndex]?.[
            prevDay
              ? periodsBtnsRef.current[prevDayIndex]?.length - 1
              : prevPeriodIndex
          ]?.focus();
          break;
        }
        case 'Tab': {
          event.preventDefault();
          tabListBtnRef.current?.[1]?.focus();
          break;
        }
        default:
          break;
      }
    };

    return (
      <>
        <DateTimePicker
          {...timePickerProps}
          label={label}
          value={value ?? null}
          onChange={handleChangeValue}
          onOpen={onOpen}
          onClose={onClose}
          open={false}
          inputProps={{
            ...inputProps,
            ref: refs,
            id,
            'aria-expanded': isOpen ? 'true' : undefined,
            'aria-controls': isOpen ? `${id}-menu` : undefined,
            'aria-haspopup': 'true',
            onClick: onOpen,
            sx: {
              ...inputProps?.sx,
              '.MuiInputAdornment-root': {
                pointerEvents: 'none',
              },
            },
          }}
        />
        {/* biome-ignore lint/a11y/useValidAriaRole: <explanation> */}
        <Popper
          ref={popperRef}
          open={isOpen}
          anchorEl={anchorEl.current}
          role={undefined}
          placement="bottom-start"
          sx={{ zIndex: 'modal' }}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={onClose}>
            <Card
              variant="soft"
              tabIndex={0}
              sx={{
                maxWidth: '85vw',
                maxHeight: '80vh',
                minHeight: 100,
                overflow: 'auto',
              }}
            >
              <LoadingPlaceholderContainer isLoading={isLoading}>
                <>
                  <CardHeader
                    sx={{ p: 0, mx: 3 }}
                    title={
                      <Tabs
                        value={tab ?? ''}
                        onChange={(_, value: string) => setTab(value ?? '')}
                      >
                        {gridData.map(({ gridIdx, name }, gridIndex) => (
                          <Tab
                            ref={(el) => {
                              if (el) {
                                tabListBtnRef.current[gridIndex] =
                                  el as unknown as HTMLButtonElement;
                              }
                            }}
                            key={gridIdx}
                            value={String(gridIdx)}
                            disableTouchRipple
                            disableRipple={false}
                            tabIndex={0}
                            label={
                              gridData.length > 1 ? name : t('calendar:period')
                            }
                          />
                        ))}
                        <Tab
                          ref={(el) => {
                            if (el) {
                              tabListBtnRef.current[gridData.length] =
                                el as unknown as HTMLButtonElement;
                            }
                          }}
                          disableTouchRipple
                          disableRipple={false}
                          tabIndex={0}
                          value="time"
                          label={t('calendar:time')}
                        />
                      </Tabs>
                    }
                  />
                  {filteredPeriods
                    .map((grid) => {
                      return tab === String(grid.gridIdx ?? '') ? (
                        <Card
                          variant="outlined"
                          key={grid.gridIdx}
                          sx={{ p: 0 }}
                        >
                          <TableContainer sx={{ borderRadius: 1 }}>
                            <Table
                              size="small"
                              sx={({ spacing }) => ({
                                '& th': {
                                  background: 'transparent',
                                  fontWeight: 600,
                                },
                                '& td': {
                                  verticalAlign: 'top',
                                  p: spacing(0.5),
                                },
                              })}
                            >
                              <TableHead>
                                <TableRow>
                                  {grid.days?.map((day) => (
                                    <TableCell key={day?.dayIdx}>
                                      {dayjs()
                                        .set('day', day?.isoDayOfWeek ?? 0)
                                        .format('dddd')}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  {grid.days?.map((day, dayIndex) => (
                                    <TableCell key={day?.dayIdx}>
                                      <List sx={{ p: 0 }}>
                                        {day?.periods.map(
                                          (period, periodIndex) => {
                                            const gridValue = getGridValue({
                                              isoDayOfWeek: day.isoDayOfWeek,
                                              startTime:
                                                periodType === 'start'
                                                  ? period.startTime
                                                  : period.endTime,
                                            });

                                            const isSelected =
                                              filterDay && value
                                                ? isSameHourAndMinute(
                                                    value,
                                                    gridValue,
                                                  )
                                                : value?.isSame(gridValue);

                                            return (
                                              <ListItem
                                                key={period.periodIdx}
                                                disableGutters
                                                dense
                                                sx={{ py: 0.25 }}
                                              >
                                                <Tooltip
                                                  title={period.name}
                                                  followCursor
                                                  disableFocusListener
                                                  placement="right"
                                                >
                                                  <ListItemButton
                                                    ref={(el) => {
                                                      if (el) {
                                                        periodsBtnsRef.current[
                                                          dayIndex
                                                        ] ||= [];
                                                        periodsBtnsRef.current[
                                                          dayIndex
                                                        ][periodIndex] =
                                                          el as unknown as HTMLButtonElement;
                                                      }
                                                    }}
                                                    onKeyDown={(event) =>
                                                      handleKeyDown(
                                                        event,
                                                        dayIndex,
                                                        periodIndex,
                                                      )
                                                    }
                                                    autoFocus={
                                                      isSelected ||
                                                      (dayIndex === 0 &&
                                                        periodIndex === 0)
                                                    }
                                                    aria-selected={isSelected}
                                                    selected={isSelected}
                                                    onClick={() => {
                                                      handleChangeValue(
                                                        gridValue,
                                                      );
                                                      onClose();
                                                    }}
                                                    sx={({ palette }) => ({
                                                      borderRadius: 1,
                                                      border: `1px solid ${
                                                        isSelected
                                                          ? palette.primary.main
                                                          : 'transparent'
                                                      }`,
                                                      backgroundColor:
                                                        isSelected
                                                          ? palette.primary
                                                              .lighter
                                                          : undefined,
                                                    })}
                                                  >
                                                    <ListItemIcon
                                                      sx={{ mr: 1 }}
                                                    >
                                                      {
                                                        iconByGridType[
                                                          period.type
                                                        ]
                                                      }
                                                    </ListItemIcon>
                                                    <ListItemText
                                                      slotProps={{
                                                        primary: {
                                                          variant: 'body2',
                                                          noWrap: true,
                                                        },
                                                      }}
                                                      primary={`${period.startTime} - ${period.endTime}`}
                                                    />
                                                  </ListItemButton>
                                                </Tooltip>
                                              </ListItem>
                                            );
                                          },
                                        )}
                                      </List>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Card>
                      ) : null;
                    })
                    .filter(Boolean)}
                  {tab === 'time' && (
                    <Card variant="outlined" sx={{ p: 0 }}>
                      <MultiSectionDigitalClock
                        ref={clockRef}
                        value={value ?? null}
                        onChange={(v) => {
                          handleChangeValue(v);
                          onClose();
                        }}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      />
                    </Card>
                  )}
                </>
              </LoadingPlaceholderContainer>
            </Card>
          </ClickAwayListener>
        </Popper>
      </>
    );
  },
);

if (process.env.NODE_ENV !== 'production') {
  SelectGridPeriod.displayName = 'SelectGridPeriod';
}

type RHFSelectGridPeriodProps<TField extends FieldValues> = Omit<
  SelectGridPeriodProps,
  'onChange'
> & {
  controlProps: UseControllerProps<TField>;
};

export const RHFSelectGridPeriod = <TField extends FieldValues>({
  label,
  timePickerProps,
  inputProps,
  controlProps,
  ...selectGridPeriodProps
}: RHFSelectGridPeriodProps<TField>) => {
  const {
    field: { ref, onBlur, name, value, onChange, disabled },
    fieldState: { error },
  } = useController(controlProps);

  return (
    <SelectGridPeriod
      label={label}
      value={value}
      onChange={onChange}
      {...selectGridPeriodProps}
      timePickerProps={{
        ...timePickerProps,
        inputRef: ref,
        disabled,
      }}
      inputProps={{
        ...inputProps,
        onBlur,
        name,
        error: !!error,
        helperText: error?.message,
      }}
    />
  );
};
